import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Bot, Send, User, Truck, Package, DollarSign, 
  MapPin, Loader2, MessageSquare, Zap, X, Minimize2, Maximize2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AIDispatcherChat = ({ userType = 'driver', userId = null, isEmbedded = false }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm the Tempus AI Dispatcher. I can help you find loads, match with drivers, get rate estimates, and answer questions about the trucking industry. How can I assist you today?",
      timestamp: new Date().toISOString()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isOpen, setIsOpen] = useState(!isEmbedded);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${API}/api/ai-dispatcher/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: input,
          user_type: userType,
          user_id: userId
        })
      });

      const data = await response.json();

      const assistantMessage = {
        role: 'assistant',
        content: data.response || 'Sorry, I encountered an error. Please try again.',
        timestamp: data.timestamp || new Date().toISOString(),
        suggestedActions: data.suggested_actions
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered a connection error. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    const actionMessages = {
      'find_loads': 'Show me available loads in my area',
      'check_rates': 'What are the current market rates for dry van loads?',
      'find_drivers': 'Help me find drivers for my load',
      'negotiate': 'I want to negotiate a rate'
    };
    
    if (actionMessages[action]) {
      setInput(actionMessages[action]);
    }
  };

  // Floating chat bubble for embedded mode
  if (isEmbedded && !isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-2xl"
        >
          <Bot className="w-8 h-8 text-white" />
        </Button>
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse" />
      </div>
    );
  }

  const chatContent = (
    <>
      {/* Header */}
      <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-6 h-6" />
          </div>
          <div>
            <CardTitle className="text-lg font-bold">AI Dispatcher</CardTitle>
            <p className="text-xs text-blue-100">Powered by GPT-5.2</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-green-500 text-white text-xs">Online</Badge>
          {isEmbedded && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMinimized(!isMinimized)}
                className="text-white hover:bg-white/20 p-1"
              >
                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-white/20 p-1"
              >
                <X className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </CardHeader>

      {!isMinimized && (
        <>
          {/* Quick Actions */}
          <div className="p-3 bg-zinc-800 border-b border-zinc-700 flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickAction('find_loads')}
              className="text-xs bg-zinc-700 border-zinc-600 hover:bg-zinc-600"
            >
              <Package className="w-3 h-3 mr-1" /> Find Loads
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickAction('check_rates')}
              className="text-xs bg-zinc-700 border-zinc-600 hover:bg-zinc-600"
            >
              <DollarSign className="w-3 h-3 mr-1" /> Check Rates
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickAction('find_drivers')}
              className="text-xs bg-zinc-700 border-zinc-600 hover:bg-zinc-600"
            >
              <Truck className="w-3 h-3 mr-1" /> Find Drivers
            </Button>
          </div>

          {/* Messages */}
          <CardContent className="p-0">
            <div className="h-80 overflow-y-auto p-4 space-y-4 bg-zinc-900">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' ? 'bg-blue-600' : 'bg-zinc-700'
                  }`}>
                    {msg.role === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-blue-400" />
                    )}
                  </div>
                  <div className={`max-w-[75%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`p-3 rounded-lg ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-zinc-800 text-zinc-100'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    {msg.suggestedActions && msg.suggestedActions.length > 0 && (
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {msg.suggestedActions.map((action, aIdx) => (
                          <Button
                            key={aIdx}
                            variant="outline"
                            size="sm"
                            className="text-xs bg-zinc-800 border-zinc-600 hover:bg-zinc-700"
                          >
                            <Zap className="w-3 h-3 mr-1" /> {action.label}
                          </Button>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-zinc-500 mt-1">
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="bg-zinc-800 p-3 rounded-lg">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 bg-zinc-800 border-t border-zinc-700">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about loads, rates, drivers..."
                  className="flex-1 bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-400"
                  disabled={loading}
                />
                <Button 
                  type="submit" 
                  disabled={loading || !input.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </>
      )}
    </>
  );

  if (isEmbedded) {
    return (
      <div className="fixed bottom-6 right-6 z-50 w-96 shadow-2xl rounded-lg overflow-hidden">
        <Card className="bg-zinc-900 border-zinc-700 m-0">
          {chatContent}
        </Card>
      </div>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800 w-full max-w-2xl mx-auto">
      {chatContent}
    </Card>
  );
};

export default AIDispatcherChat;
